'use client';

import { motion } from 'framer-motion';
import { useCallback } from 'react';

import { FanficReviews } from '@/components/fanfic-reviews';
import { ChaptersTab } from '@/components/fanfic-page/chapters-tab';
import { FanficInfoBar } from '@/features/fanfic-page/components/fanfic-info-bar';
import { TWork, type TChapterWithoutContent } from '@/features/fanfic-page/types/fanfic';

export interface FanficTabsProps {
  activeTab: 'description' | 'chapters' | 'reviews';
  setActiveTab: (tab: 'description' | 'chapters' | 'reviews') => void;
  fanfic: TWork;
  selectedChapterId: string | null;
  progressByChapter: { chapterIndex: number; progress: number }[];
  updateReadingProgress: (fanficId: string, chapterIndex: number, progress: number) => void;
  onChapterSelect: (chapterId: string) => void;
  onOpenFullscreen: (chapterId: string) => void;
  timeToRead: string;
  preview: boolean;
  onReviewsHasNextPageChange?: (hasNextPage: boolean) => void;
  focusReviewComposer?: boolean;
  highlightReviewId?: number | null;
  initialChapters?: TChapterWithoutContent[];
}

/**
 * FanficTabs component - displays tabs with description, chapters and reviews
 * Progressive enhancement: description visible by default without JS for SEO
 * Chapters are loaded from parent component via props
 */
export function FanficTabs({
  activeTab,
  setActiveTab,
  fanfic,
  selectedChapterId,
  progressByChapter,
  updateReadingProgress,
  onOpenFullscreen,
  timeToRead,
  preview,
  onReviewsHasNextPageChange,
  focusReviewComposer = false,
  highlightReviewId = null,
  initialChapters = [],
}: FanficTabsProps) {
  const handleDescriptionClick = useCallback(() => {
    setActiveTab('description');
  }, [setActiveTab]);

  const handleChaptersClick = useCallback(() => {
    setActiveTab('chapters');
  }, [setActiveTab]);

  const handleReviewsClick = useCallback(() => {
    setActiveTab('reviews');
  }, [setActiveTab]);

  return (
    <div className="bg-white rounded-t-2xl overflow-hidden w-full flex flex-col min-h-0">
      <div className="border-b border-[#dee1ea] mb-0">
        <fieldset
          disabled={preview}
          className="flex disabled:pointer-events-none disabled:opacity-70"
        >
          <motion.button
            className={`flex-1 pb-3 text-center font-medium relative ${
              activeTab === 'description' ? 'text-[#1B1D22]' : 'text-[#6e7384] hover:text-[#111827]'
            }`}
            onClick={handleDescriptionClick}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Описание
            {activeTab === 'description' && (
              <motion.div
                className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#ff3dae]"
                layoutId="activeTab"
                initial={false}
                transition={{
                  type: 'spring',
                  stiffness: 500,
                  damping: 30,
                }}
              />
            )}
          </motion.button>

          <motion.button
            className={`flex-1 pb-3 text-center font-medium relative ${
              activeTab === 'chapters' ? 'text-[#1B1D22]' : 'text-[#6e7384] hover:text-[#111827]'
            }`}
            onClick={handleChaptersClick}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="flex items-center justify-center gap-2">
              Главы{' '}
              <span className="bg-[#F2F3F7] text-[#6e7384] px-2 py-0.5 rounded text-[12px]">
                {fanfic.chaptersCount ?? 0}
              </span>
            </div>
            {activeTab === 'chapters' && (
              <motion.div
                className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#ff3dae]"
                layoutId="activeTab"
                initial={false}
                transition={{
                  type: 'spring',
                  stiffness: 500,
                  damping: 30,
                }}
              />
            )}
          </motion.button>

          <motion.button
            className={`flex-1 pb-3 text-center font-medium relative ${
              activeTab === 'reviews' ? 'text-[#1B1D22]' : 'text-[#6e7384] hover:text-[#111827]'
            }`}
            onClick={handleReviewsClick}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="flex items-center justify-center gap-2">
              Отзывы{' '}
              <span className="bg-[#F2F3F7] text-[#6e7384] px-2 py-0.5 rounded text-[12px]">
                {fanfic.statistics?.commentsCount ?? 0}
              </span>
            </div>
            {activeTab === 'reviews' && (
              <motion.div
                className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#ff3dae]"
                layoutId="activeTab"
                initial={false}
                transition={{
                  type: 'spring',
                  stiffness: 500,
                  damping: 30,
                }}
              />
            )}
          </motion.button>
        </fieldset>
      </div>

      {/* Description tab - visible by default for SSR/SEO */}
      <motion.div
        className={`space-y-4 pt-5 min-[1000px]:pt-6 flex-1 min-h-0 ${
          activeTab === 'description' ? 'block' : 'hidden'
        }`}
        initial={false}
        animate={{
          opacity: activeTab === 'description' ? 1 : 0,
          y: activeTab === 'description' ? 0 : 20,
        }}
        transition={{ duration: 0.3 }}
      >
        <FanficInfoBar fanfic={fanfic} timeToRead={timeToRead} />
      </motion.div>

      {/*
        Chapters tab stays visually collapsed on description by default,
        but SSR chapter links remain in the HTML for crawlers / no-JS.
      */}
      <motion.div
        className={`flex-1 min-h-0 ${activeTab === 'chapters' ? 'block' : 'hidden'}`}
        initial={false}
        animate={{
          opacity: activeTab === 'chapters' ? 1 : 0,
          y: activeTab === 'chapters' ? 0 : 20,
        }}
        transition={{ duration: 0.3 }}
      >
        <ChaptersTab
          progress={progressByChapter || []}
          updateReadingProgress={updateReadingProgress}
          fanfic={fanfic}
          selectedChapterId={selectedChapterId}
          onOpenFullscreen={onOpenFullscreen}
          initialChapters={initialChapters}
        />
      </motion.div>

      {/* Reviews tab */}
      <motion.div
        className={`space-y-4 mt-5 min-[1000px]:mt-6 flex-1 min-h-0 ${
          activeTab === 'reviews' ? 'block' : 'hidden'
        }`}
        initial={false}
        animate={{
          opacity: activeTab === 'reviews' ? 1 : 0,
          y: activeTab === 'reviews' ? 0 : 20,
        }}
        transition={{ duration: 0.3 }}
      >
        <FanficReviews
          fanficId={fanfic.id}
          className=""
          isActive={activeTab === 'reviews'}
          onHasNextPageChange={onReviewsHasNextPageChange}
          focusComposer={focusReviewComposer}
          highlightCommentId={highlightReviewId}
        />
      </motion.div>
    </div>
  );
}
